"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var d3_1 = require("d3");
/**
 * 图表数据模型
 */
var DataModel = /** @class */ (function () {
    function DataModel() {
        /**
         * 原始数据
         * @since 2.0
         */
        this.raw = [];
        /**
         * 是否为堆叠数据
         * @since 2.0
         */
        this.stack = false;
        /**
         * 堆叠的类型
         * @since 2.0
         */
        this.stackType = "stack";
        /**
         * 过滤的列名
         * @private
         * @since 2.0
         */
        this.filterColumnNames = [];
        /**
         * 分类的范围，用于数据缩放
         * @private
         * @since 2.0
         */
        this.categoryRange = [];
        /**
         * 值域的缓存
         * @private
         * @since 2.0
         */
        this.extentCache = null;
    }
    /**
     * 定义数据模型，根据 Key 生成对应的行列式生成器
     *
     * @param categoryKey 原始字段用作行的字段名，对应 category
     * @param valueKey 原始数据中作为值的字段名，对应 value
     * @param serieKey 原始字段中作为列的字段名，用于分类，对应 series
     * @param sortKeys 行字段值的排序方式
     * @param stack 是否为堆积图
     *
     * @since 2.0
     */
    DataModel.prototype.define = function (categoryKey, valueKey, serieKey, colorKey, sortKeys, stack, stackType) {
        var _this = this;
        if (stack === void 0) { stack = false; }
        if (stackType === void 0) { stackType = "stack"; }
        this.valueKey = valueKey;
        this.serieKey = serieKey;
        this.categoryKey = categoryKey;
        this.colorKey = colorKey;
        this.sortKeys = sortKeys;
        this.stack = stack;
        this.stackType = stackType;
        var currentRow;
        var positive = 0;
        var negative = 0;
        // 生成行列式数据生成器
        this.determinantOperator = d3_1.nest()
            .key(function (v) {
            if (v && typeof v[categoryKey] !== "undefined") {
                return String(v[categoryKey]);
            }
            return "default";
        })
            .key(function (v) {
            if (v && typeof v[serieKey] !== "undefined") {
                return String(v[serieKey]);
            }
            return "default";
        })
            // 处理每个数据点
            .rollup(function (leaves) {
            var _a = tslib_1.__read(leaves || [], 1), _b = _a[0], first = _b === void 0 ? {} : _b;
            var category = String(first[_this.categoryKey]) || "default";
            var isFirst = currentRow !== category;
            var value = typeof first[_this.valueKey] === "number"
                ? Number(first[_this.valueKey])
                : null;
            var stackValue = value;
            if (isFirst) {
                currentRow = category;
                positive = 0;
                negative = 0;
            }
            if (value >= 0) {
                stackValue = value + positive;
                positive = stackValue;
            }
            else {
                stackValue = value + negative;
                negative = stackValue;
            }
            return {
                value: value,
                stackValue: stackValue,
                category: category,
                serieName: typeof first[_this.serieKey] !== "undefined"
                    ? String(first[_this.serieKey])
                    : "default",
                origin: first,
                colorName: typeof first[_this.colorKey] !== "undefined"
                    ? String(first[_this.colorKey])
                    : "default",
            };
        });
        this.dataTable = this.determinantOperator.object(this.raw);
        this.filterDataTable = Object.assign({}, this.dataTable);
        this.extentCache = null;
        // 每次调用 determinantOperator 需要重置累计值
        currentRow = null;
        positive = 0;
        negative = 0;
    };
    /**
     * 修改原始数据
     * @param dataSource 原始数据
     * @since 2.0
     */
    DataModel.prototype.source = function (dataSource) {
        if (dataSource && Array.isArray(dataSource)) {
            // 过滤掉无效数据
            this.raw = dataSource.filter(function (i) { return !!i; });
        }
    };
    /**
     * 根据列字段值过滤数据表
     * @param serieNames 保留数据的列字段值
     * @since 2.0
     */
    DataModel.prototype.filterColumns = function (serieNames) {
        var _this = this;
        this.filterColumnNames = serieNames;
        var filterDataSource = this.raw.filter(function (item) { return !_this.filterColumnNames.includes(String(item[_this.serieKey])); });
        this.filterDataTable = this.determinantOperator.object(filterDataSource);
        if (this.categoryRange) {
            // 重新设置过滤的时候还需要处理 Range
            this.range(this.categoryRange);
        }
        this.extentCache = null;
    };
    /**
     * 设置保留的行
     * @param categories 保留的行
     * @since 2.0
     */
    DataModel.prototype.range = function (categories) {
        this.categoryRange = categories;
        if (Array.isArray(categories) && categories.length > 0) {
            var newDataTable = {};
            this.filterDataTable = {};
            for (var i = 0; i < categories.length; i++) {
                var key = categories[i];
                if (this.dataTable[key]) {
                    newDataTable[key] = this.dataTable[key];
                }
                this.filterDataTable[key] = newDataTable[key];
            }
            this.dataTable = newDataTable;
            this.extentCache = null;
        }
    };
    /**
     * 获取列名
     * @param getAll 是否包含过滤的列
     * @since 2.0
     */
    DataModel.prototype.getSerieNames = function (getAll) {
        var _this = this;
        var cols = d3_1.nest()
            .key(function (d) {
            return typeof d[_this.serieKey] !== "undefined"
                ? String(d[_this.serieKey])
                : "default";
        })
            .entries(this.raw)
            .map(function (col) { return col.key; });
        if (getAll)
            return cols;
        return cols.filter(function (col) { return !_this.filterColumnNames.includes(col); });
    };
    /**
     * 获取行名
     * @since 2.0
     */
    DataModel.prototype.getCategoryNames = function (allData) {
        var rows = Object.keys(allData ? this.dataTable : this.filterDataTable);
        if (typeof this.sortKeys === "function") {
            // 优先使用 sortKeys 排序
            return rows.sort(this.sortKeys);
        }
        // row 已无序(Object.keys 以任意顺序返回 keys), 这里使用原始顺序
        // this.dataTable/filterDataTable 从 determinantOperator.object(this.raw)
        // 调用中而来, 我们以相同的生成步骤处理 raw 得到语义相同且顺序一致的结构
        var originRows = this.determinantOperator
            .entries(this.raw)
            .map(function (row) { return row.key; });
        // 保证使用部分数据时, 元素的顺序仍然是稳定的
        return rows.sort(function (a, b) { return originRows.indexOf(a) - originRows.indexOf(b); });
    };
    /**
     * 根据字段名获取原始数据中所有的字段值
     * @param key 字段名
     */
    DataModel.prototype.getKeyNames = function (key) {
        var cols = d3_1.nest()
            .key(function (d) { return (typeof d[key] !== "undefined" ? String(d[key]) : "default"); })
            .object(this.raw);
        return Object.keys(cols);
    };
    /**
     * 计算值域
     * @param autoFit 是否获取最接近的阈值而非从 0 开始
     * @since 2.0
     */
    DataModel.prototype.getExtent = function (autoFit) {
        var e_1, _a, e_2, _b;
        if (this.extentCache !== null)
            return this.extentCache;
        var extentMin = autoFit ? null : 0;
        var extentMax = 0;
        var rows = Object.values(this.filterDataTable);
        try {
            for (var rows_1 = tslib_1.__values(rows), rows_1_1 = rows_1.next(); !rows_1_1.done; rows_1_1 = rows_1.next()) {
                var row = rows_1_1.value;
                if (row) {
                    var cols = Object.values(row);
                    try {
                        for (var cols_1 = (e_2 = void 0, tslib_1.__values(cols)), cols_1_1 = cols_1.next(); !cols_1_1.done; cols_1_1 = cols_1.next()) {
                            var col = cols_1_1.value;
                            var value = col.value, stackValue = col.stackValue;
                            var v = this.stack ? stackValue : value;
                            if (v > extentMax)
                                extentMax = v;
                            if ((v < extentMin || extentMin === null) && v !== null)
                                extentMin = v;
                        }
                    }
                    catch (e_2_1) { e_2 = { error: e_2_1 }; }
                    finally {
                        try {
                            if (cols_1_1 && !cols_1_1.done && (_b = cols_1.return)) _b.call(cols_1);
                        }
                        finally { if (e_2) throw e_2.error; }
                    }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (rows_1_1 && !rows_1_1.done && (_a = rows_1.return)) _a.call(rows_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        if (extentMin === extentMax) {
            extentMin = 0;
        }
        if (autoFit) {
            extentMin -= Math.round(extentMin * 0.1);
        }
        this.extentCache = [
            // 如果是自动适应或者最小值小于 0 则使用自适应值，否则以 0 作为最小值
            autoFit || extentMin < 0 ? extentMin || 0 : 0,
            extentMax || 10,
        ];
        return this.extentCache;
    };
    /**
     * 获取指定行列的值
     * @param categoryKey 行字段值
     * @param serieKey 列字段值
     * @param originValue 显示原始值
     * @param allData 使用全部的数据
     * @since 2.0
     */
    DataModel.prototype.get = function (categoryKey, serieKey, originValue) {
        if (this.filterDataTable[categoryKey] === undefined)
            return null;
        var _a = this.filterDataTable[categoryKey][serieKey] || {}, _b = _a.value, value = _b === void 0 ? null : _b, _c = _a.stackValue, stackValue = _c === void 0 ? null : _c;
        return this.stack && !originValue ? stackValue : value;
    };
    /**
     * 获取指定行列的值
     * @param categoryKey 行字段值
     * @param serieKey 列字段值
     * @param originValue 显示原始值
     * @param allData 使用全部的数据
     * @since 2.0
     */
    DataModel.prototype.getByAll = function (categoryKey, serieKey, originValue) {
        if (this.dataTable[categoryKey] === undefined)
            return null;
        var _a = this.dataTable[categoryKey][serieKey] || {}, _b = _a.value, value = _b === void 0 ? null : _b, _c = _a.stackValue, stackValue = _c === void 0 ? null : _c;
        return this.stack && !originValue ? stackValue : value;
    };
    /**
     * 根据行字段的值获取某行数据
     * @param categoryKey 行字段值
     *
     * @todo 数据缓存
     */
    DataModel.prototype.getDataFromCategoryName = function (categoryKey, withPercent, allData) {
        var values = [];
        var dataTable = allData ? this.dataTable : this.filterDataTable;
        if (dataTable[categoryKey]) {
            values = Object.values(dataTable[categoryKey]);
        }
        var total = d3_1.sum(values.map(function (data) {
            return typeof data.value === "number" ? data.value : 0;
        }));
        if (withPercent) {
            var stackPercent_1 = 0;
            return values.map(function (leaf) {
                var percent = (leaf.value / total) * 100;
                stackPercent_1 += percent;
                return tslib_1.__assign(tslib_1.__assign({}, leaf), { percent: percent,
                    stackPercent: stackPercent_1 });
            });
        }
        return values;
    };
    /**
     * 根据列字段的值获取某列数据
     * @param serieKey 列字段值
     *
     * @todo 数据缓存
     */
    DataModel.prototype.getDataFromSerieName = function (serieKey) {
        var e_3, _a;
        var values = [];
        var cateValues = Object.values(this.dataTable);
        try {
            for (var cateValues_1 = tslib_1.__values(cateValues), cateValues_1_1 = cateValues_1.next(); !cateValues_1_1.done; cateValues_1_1 = cateValues_1.next()) {
                var v = cateValues_1_1.value;
                if (v[serieKey]) {
                    values.push(v[serieKey]);
                }
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (cateValues_1_1 && !cateValues_1_1.done && (_a = cateValues_1.return)) _a.call(cateValues_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
        return values;
    };
    /**
     * 获取当前生效的全部数据
     */
    DataModel.prototype.getAllData = function () {
        var e_4, _a;
        var values = [];
        var serieNames = this.getSerieNames();
        try {
            for (var serieNames_1 = tslib_1.__values(serieNames), serieNames_1_1 = serieNames_1.next(); !serieNames_1_1.done; serieNames_1_1 = serieNames_1.next()) {
                var serie = serieNames_1_1.value;
                values.push(this.getDataFromSerieName(serie));
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (serieNames_1_1 && !serieNames_1_1.done && (_a = serieNames_1.return)) _a.call(serieNames_1);
            }
            finally { if (e_4) throw e_4.error; }
        }
        return values;
    };
    return DataModel;
}());
exports.DataModel = DataModel;
//# sourceMappingURL=data-model.js.map